import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import RateReviewIcon from "@mui/icons-material/RateReview";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import LinearProgress from "@mui/material/LinearProgress";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useTranslation } from "hooks/useTranslation";
import { DateTimeCell } from "components/DateCells";
import { EXAM_STATUS_LABEL_KEYS } from "constants/examStatus";
import { getStudentExams, exportAnnotations } from "services/sampleService";

const QA_STATUS_OPTIONS = [
  { value: "", labelKey: "qa.filters.anyStatus" },
  { value: "Pending", labelKey: "qa.status.pending" },
  { value: "Partially Approved", labelKey: "qa.status.partiallyApproved" },
  { value: "Approved", labelKey: "qa.status.approved" },
  { value: "Rejected", labelKey: "qa.status.rejected" },
];

function translateExamStatusLabel(status, t) {
  if (!status) return "";
  const key = EXAM_STATUS_LABEL_KEYS[status];
  return key ? t(key, status) : status;
}

function translateQaStatusLabel(status, t) {
  if (!status || status === "Pending") return t("qa.status.pending", "Pending");
  if (status === "Approved") return t("qa.status.approved", "Approved");
  if (status === "Rejected") return t("qa.status.rejected", "Rejected");
  if (status === "Partially Approved") {
    return t("qa.status.partiallyApproved", "Partially Approved");
  }
  return status;
}

function qaStatusChipColor(status) {
  if (status === "Approved") return "success";
  if (status === "Rejected") return "error";
  if (status === "Partially Approved") return "info";
  return "warning";
}

function QaStatusCell({ value }) {
  const { t } = useTranslation();
  const normalized = value || "Pending";

  return (
    <Chip
      label={translateQaStatusLabel(normalized, t)}
      color={qaStatusChipColor(normalized)}
      size="small"
      sx={{ fontWeight: 500 }}
    />
  );
}

QaStatusCell.propTypes = {
  value: PropTypes.string,
};

function QaStatusBreakdownCell({ row }) {
  const { t } = useTranslation();
  const { qa_status, qa1_status, qa2_status } = row.original;

  return (
    <Box>
      <QaStatusCell value={qa_status} />
      {(qa1_status || qa2_status) && (
        <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
          {t("exams.table.annotator1", "Annotator 1")}:{" "}
          {translateQaStatusLabel(qa1_status || "Pending", t)}
          {" · "}
          {t("exams.table.annotator2", "Annotator 2")}:{" "}
          {translateQaStatusLabel(qa2_status || "Pending", t)}
        </Typography>
      )}
    </Box>
  );
}

QaStatusBreakdownCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      qa_status: PropTypes.string,
      qa1_status: PropTypes.string,
      qa2_status: PropTypes.string,
    }).isRequired,
  }).isRequired,
};

function OverallStatusCell({ value }) {
  const { t } = useTranslation();
  const getColor = (status) => {
    switch (status) {
      case "Annotated Once":
        return "primary";
      case "Annotated Twice":
        return "success";
      default:
        return "default";
    }
  };

  return (
    <Chip
      label={translateExamStatusLabel(value, t)}
      color={getColor(value)}
      size="small"
      sx={{ fontWeight: 500 }}
    />
  );
}

OverallStatusCell.propTypes = {
  value: PropTypes.string.isRequired,
};

function ReviewActionCell({ row }) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <MDButton
      size="small"
      color="info"
      variant="outlined"
      startIcon={<RateReviewIcon fontSize="small" />}
      onClick={() =>
        navigate(
          `/admin/exams/view-sample?exam_id=${encodeURIComponent(row.original.exam_id)}&qa=1`
        )
      }
      sx={{ whiteSpace: "nowrap" }}
    >
      {t("qa.buttons.review", "Review")}
    </MDButton>
  );
}

ReviewActionCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      exam_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }).isRequired,
  }).isRequired,
};

function ReviewedOnCell({ value }) {
  return <DateTimeCell value={value} fallback="—" />;
}

ReviewedOnCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.instanceOf(Date)]),
};

function QaCommentCell({ value }) {
  const text = value || "—";
  if (text === "—") return text;
  return (
    <Tooltip title={text} placement="top-start">
      <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
        {text}
      </Typography>
    </Tooltip>
  );
}

QaCommentCell.propTypes = {
  value: PropTypes.string,
};

function QaIndex() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState({
    qa_status: "",
    exam_id: "",
    page: 1,
    limit: 25,
    completed_only: true,
    include_qa_stats: true,
  });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [rowsData, setRowsData] = useState([]);
  const [total, setTotal] = useState(0);
  const [qaStats, setQaStats] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, severity: "success", message: "" });

  const showSnackbar = ({ severity = "success", message = "" }) => {
    setSnackbar({ open: true, severity, message });
    setTimeout(() => setSnackbar((s) => ({ ...s, open: false })), 4000);
  };

  const qaStatusOptions = QA_STATUS_OPTIONS.map(({ value, labelKey }) => ({
    value,
    label: value ? t(labelKey, value) : t("qa.filters.anyStatus", "Any QA status"),
  }));

  const columns = useMemo(
    () => [
      {
        Header: t("exams.table.examId", "Session ID"),
        accessor: "exam_id",
        align: "left",
      },
      {
        Header: t("exams.table.examDate", "Session Date"),
        accessor: "start_time",
        align: "left",
        Cell: DateTimeCell,
      },
      {
        Header: t("exams.table.annotator1Details", "Annotator 1"),
        accessor: "annotator1_name",
        align: "left",
      },
      {
        Header: t("exams.table.annotator2Details", "Annotator 2"),
        accessor: "annotator2_name",
        align: "left",
      },
      {
        Header: t("exams.table.overallStatus", "Overall Status"),
        accessor: "overall_status",
        align: "left",
        Cell: OverallStatusCell,
      },
      {
        Header: t("qa.table.qaStatus", "QA Status"),
        accessor: "qa_status",
        align: "left",
        Cell: QaStatusBreakdownCell,
      },
      {
        Header: t("qa.table.qaComment", "QA comment"),
        accessor: "qa_comment",
        align: "left",
        Cell: QaCommentCell,
      },
      {
        Header: t("qa.table.reviewedBy", "Reviewed by"),
        accessor: "qa_reviewed_by_name",
        align: "left",
      },
      {
        Header: t("qa.table.reviewedOn", "Reviewed on"),
        accessor: "qa_reviewed_on",
        align: "left",
        Cell: ReviewedOnCell,
      },
      {
        Header: t("exams.table.actions", "Actions"),
        accessor: "actions",
        align: "center",
        Cell: ReviewActionCell,
      },
    ],
    [t]
  );

  const fetchQaExams = async (useFilters = filters) => {
    try {
      setLoading(true);
      const resp = await getStudentExams(useFilters);
      const mapped = (resp.data || []).map((e) => ({
        _row_id: e.id ?? e.exam_id,
        exam_id: e.exam_id ?? "",
        start_time: e.start_time ?? "",
        annotator1_name: e.annotator1_name || t("exams.table.none", "None"),
        annotator2_name: e.annotator2_name || t("exams.table.none", "None"),
        overall_status: e.status ?? "",
        qa_status: e.qa_status || "Pending",
        qa1_status: e.qa1_status || "",
        qa2_status: e.qa2_status || "",
        qa_comment: e.qa_comment || "",
        qa_reviewed_by_name: e.qa_reviewed_by_name || "—",
        qa_reviewed_on: e.qa_reviewed_on ?? "",
      }));
      setRowsData(mapped);
      setTotal(resp.total_records ?? mapped.length);
      setQaStats(resp.qa_stats ?? null);
    } catch (err) {
      console.error("fetchQaExams error:", err);
      setRowsData([]);
      setTotal(0);
      showSnackbar({
        severity: "error",
        message: t("qa.notifications.fetchError", "Failed to load QA queue"),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        const resp = await getStudentExams(filters);
        if (cancelled) return;

        const mapped = (resp.data || []).map((e) => ({
          _row_id: e.id ?? e.exam_id,
          exam_id: e.exam_id ?? "",
          start_time: e.start_time ?? "",
          annotator1_name: e.annotator1_name || t("exams.table.none", "None"),
          annotator2_name: e.annotator2_name || t("exams.table.none", "None"),
          overall_status: e.status ?? "",
          qa_status: e.qa_status || "Pending",
          qa_comment: e.qa_comment || "",
          qa_reviewed_by_name: e.qa_reviewed_by_name || "—",
          qa_reviewed_on: e.qa_reviewed_on ?? "",
        }));
        setRowsData(mapped);
        setTotal(resp.total_records ?? mapped.length);
        setQaStats(resp.qa_stats ?? null);
      } catch (err) {
        if (cancelled) return;
        console.error("fetchQaExams error:", err);
        setRowsData([]);
        setTotal(0);
        showSnackbar({
          severity: "error",
          message: t("qa.notifications.fetchError", "Failed to load QA queue"),
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [filters, t]);

  const onApply = () => {
    const next = { ...filters, page: 1 };
    setFilters(next);
    fetchQaExams(next);
  };

  const onReset = () => {
    const reset = {
      qa_status: "",
      exam_id: "",
      page: 1,
      limit: 25,
      completed_only: true,
      include_qa_stats: true,
    };
    setFilters(reset);
    fetchQaExams(reset);
  };

  const onPageChange = (newPage) => {
    const next = { ...filters, page: newPage };
    setFilters(next);
  };

  const onExport = async () => {
    try {
      setExporting(true);
      await exportAnnotations({
        qa_status: filters.qa_status || undefined,
        exam_id: filters.exam_id || undefined,
      });
      showSnackbar({
        severity: "success",
        message: t("qa.notifications.exportSuccess", "Annotations exported successfully"),
      });
    } catch (err) {
      showSnackbar({
        severity: "error",
        message: err.message || t("qa.notifications.exportError", "Failed to export annotations"),
      });
    } finally {
      setExporting(false);
    }
  };

  const reviewed = Number(qaStats?.reviewed ?? 0);
  const totalCompleted = Number(qaStats?.total_completed ?? 0);
  const target30 = Number(qaStats?.target_30_percent ?? 0);
  const progressPercent =
    totalCompleted > 0 ? Math.min(100, Math.round((reviewed / totalCompleted) * 100)) : 0;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <MDTypography variant="h5" fontWeight="medium">
                {t("qa.title", "Quality Assurance")}
              </MDTypography>
              <MDButton
                color="info"
                variant="gradient"
                startIcon={<FileDownloadIcon />}
                onClick={onExport}
                disabled={exporting}
              >
                {exporting
                  ? t("qa.buttons.exporting", "Exporting…")
                  : t("qa.buttons.exportCsv", "Export CSV")}
              </MDButton>
            </MDBox>

            <Card sx={{ p: 2, mb: 3 }}>
              <MDTypography variant="button" fontWeight="medium" gutterBottom>
                {t("qa.progress.title", "QA progress")}
              </MDTypography>
              <MDTypography variant="body2" color="text" mb={1}>
                {t(
                  "qa.progress.summary",
                  "Reviewed {{reviewed}} / {{total}} completed exams (target 30% = {{target}})",
                  { reviewed, total: totalCompleted, target: target30 }
                )}
              </MDTypography>
              <LinearProgress
                variant="determinate"
                value={progressPercent}
                sx={{ height: 8, borderRadius: 1 }}
              />
              <MDTypography variant="caption" color="text.secondary" mt={0.5} display="block">
                {t(
                  "qa.progress.hint",
                  "Select any completed exam to review. The 30% target is a guideline."
                )}
              </MDTypography>
            </Card>

            <Card sx={{ p: 2, mb: 3 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t("exams.filters.sessionId", "Session ID")}
                    value={filters.exam_id}
                    onChange={(e) => setFilters((f) => ({ ...f, exam_id: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label={t("qa.filters.qaStatus", "QA status")}
                    value={filters.qa_status}
                    onChange={(e) => setFilters((f) => ({ ...f, qa_status: e.target.value }))}
                  >
                    {qaStatusOptions.map((opt) => (
                      <MenuItem key={opt.value || "any"} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <MDBox display="flex" gap={1}>
                    <MDButton variant="gradient" color="info" onClick={onApply} disabled={loading}>
                      {t("exams.buttons.apply", "Apply")}
                    </MDButton>
                    <MDButton
                      variant="outlined"
                      color="secondary"
                      onClick={onReset}
                      disabled={loading}
                    >
                      {t("exams.buttons.reset", "Reset")}
                    </MDButton>
                  </MDBox>
                </Grid>
              </Grid>
            </Card>

            <Card
              sx={{
                boxShadow: 4,
                borderRadius: 2,
                overflow: "hidden",
                backgroundColor: "#f9fafe",
                border: "1px solid #e0e0e0",
              }}
            >
              <MDBox>
                <DataTable
                  table={{ columns, rows: rowsData }}
                  isSorted
                  canSearch
                  showTotalEntries
                  totalRecords={total}
                  entriesPerPage={{
                    defaultValue: 25,
                    entries: [25, 50, 100, 250],
                    onChange: (value) => {
                      setFilters((f) => ({ ...f, limit: value, page: 1 }));
                    },
                  }}
                  pagination={{
                    variant: "gradient",
                    color: "info",
                    page: filters.page - 1,
                    onPageChange: (_, newPage) => onPageChange(newPage + 1),
                  }}
                  noEndBorder={false}
                />
              </MDBox>
            </Card>

            <MDBox mt={1} textAlign="right" color="text">
              <MDTypography variant="caption">
                {t("exams.table.totalRecords", "Total Records")}: {total}
              </MDTypography>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      <Snackbar
        open={snackbar.open}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default QaIndex;
